import { useMemo } from "react";
import type { ErrorLike } from "@apollo/client";
import {
  UNASSIGNED_GROUP_KEY,
  buildGroupWhere,
} from "#app/components/issues/issue-grouping";
import { useScopedWhere } from "#app/widgets/dashboards/scope/use-scoped-where";
import { ARCHIVED_OUT } from "#app/widgets/dashboards/use-dimension-slices";
import { useIssueGroupCounts } from "#app/hooks/issue-list/use-issue-group-counts";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { combineWhereConditions } from "#app/lib/query-utils";
import type { MemberBreakdown } from "./use-team-member-insights";
import type { Omittable } from "@salestrak/ui";

/**
 * ONE expanded member's data layer, all exact and uncapped: the metric/filter
 * breakdowns come from server `issueGroupCounts` aggregations over the member's
 * scoped assignment predicate, and `tableWhere` is the folded `where` the
 * expanded table's own windowed paged query reads — so the table reaches EVERY
 * assigned project and its row total always matches the "Assigned projects"
 * tile by construction. Runs only while a member is expanded (one at a time).
 */
export function useMemberProjects(
  userId: string,
  workflowId: string | null,
  projectTypeId: string | null,
  // The result relaxes: `error` is Apollo's own `error` field, which is
  // `undefined` while the query is healthy, and the tile reads it as a
  // truthiness check.
): Omittable<{
  /** Exact count of the member's assigned projects in the widget window. */
  assignedTotal: number;
  workflows: MemberBreakdown[];
  projectTypes: MemberBreakdown[];
  /** The expanded table's fully-folded `where` (member + menu filters). */
  tableWhere: Record<string, unknown>;
  ready: boolean;
  error?: ErrorLike;
}> {
  const { where: projectWhere } = useScopedWhere(ARCHIVED_OUT, {
    dateField: "lastUpdateDate",
  });
  const memberWhere = useMemo(
    () =>
      combineWhereConditions([projectWhere, buildGroupWhere("assignee", userId)].filter(
        (c): c is Record<string, unknown> => c != null,
      )) ?? {},
    [projectWhere, userId],
  );

  // A project has exactly one workflow, so the workflow buckets sum to the
  // exact assigned count — one aggregation feeds the tile AND the filter menu.
  const workflowCounts = useIssueGroupCounts({ where: memberWhere, groupBy: "workflowId" });
  const typeCounts = useIssueGroupCounts({ where: memberWhere, groupBy: "issueTypeId" });
  const { workflowMap, issueTypeMap } = useWorkspaceData();

  const workflows = useMemo<MemberBreakdown[]>(
    () =>
      Object.entries(workflowCounts.counts)
        .filter(([id]) => id !== UNASSIGNED_GROUP_KEY)
        .map(([id, count]) => ({
          id,
          label: workflowMap.get(id)?.name ?? "Unknown workflow",
          count,
        }))
        .sort((a, b) => b.count - a.count),
    [workflowCounts.counts, workflowMap],
  );

  const projectTypes = useMemo<MemberBreakdown[]>(
    () =>
      Object.entries(typeCounts.counts)
        .map(([id, count]) => ({
          id,
          label:
            id === UNASSIGNED_GROUP_KEY
              ? "No Project type"
              : (issueTypeMap.get(id)?.name ?? "Unknown type"),
          count,
        }))
        .sort((a, b) => b.count - a.count),
    [typeCounts.counts, issueTypeMap],
  );

  const assignedTotal = useMemo(
    () => Object.values(workflowCounts.counts).reduce((sum, n) => sum + n, 0),
    [workflowCounts.counts],
  );

  const tableWhere = useMemo(
    () =>
      combineWhereConditions(
        [
          memberWhere,
          workflowId ? buildGroupWhere("workflow", workflowId) : undefined,
          projectTypeId ? buildGroupWhere("issueType", projectTypeId) : undefined,
        ].filter((c): c is Record<string, unknown> => c != null),
      ) ?? {},
    [memberWhere, workflowId, projectTypeId],
  );

  return {
    assignedTotal,
    workflows,
    projectTypes,
    tableWhere,
    ready: workflowCounts.ready && typeCounts.ready,
    error: workflowCounts.error ?? typeCounts.error,
  };
}
